import React, { useEffect, useState, createContext, useContext } from 'react';
import { login as apiLogin, listEsimAdminUsers, createEsimAdminUser, updateEsimAdminUser, deleteEsimAdminUser } from '../services/api';

// Define the shape of our user object
interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'support';
}

// Define the shape of our auth context
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  users: User[];
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  addUser: (email: string, name: string, password: string, role: 'admin' | 'support') => Promise<boolean>;
  deleteUser: (id: string) => Promise<boolean>;
  updateUserRole: (id: string, role: 'admin' | 'support') => Promise<boolean>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
}

// Create the context with a default value
const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  users: [],
  login: async () => false,
  logout: () => {},
  addUser: async () => false,
  deleteUser: async () => false,
  updateUserRole: async () => false,
  changePassword: async () => false
});
export const AuthProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  // Initialize authentication state by checking localStorage immediately
  const [user, setUser] = useState<User | null>(() => {
    try {
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('auth_token');
      if (storedUser && storedToken) {
        const parsedUser = JSON.parse(storedUser);
        console.log('Found stored user:', parsedUser); // Debug log
        return parsedUser;
      }
    } catch (error) {
      console.error('Error retrieving stored user:', error);
      // Clear potentially corrupted data
      localStorage.removeItem('user');
      localStorage.removeItem('auth_token');
    }
    return null;
  });
  
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    try {
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('auth_token');
      return !!(storedUser && storedToken);
    } catch (error) {
      return false;
    }
  });
  
  const [users, setUsers] = useState<User[]>([]);

  // Load users from API
  useEffect(() => {
    if (isAuthenticated && user?.role === 'admin') {
      loadUsers();
    }
  }, [isAuthenticated, user]);

  // Listen for auth-expired events from API interceptor
  useEffect(() => {
    const handleAuthExpired = () => {
      console.log('Authentication expired, logging out...');
      logout();
    };

    window.addEventListener('auth-expired', handleAuthExpired);
    
    return () => {
      window.removeEventListener('auth-expired', handleAuthExpired);
    };
  }, []);

  const loadUsers = async () => {
    try {
      const result = await listEsimAdminUsers();
      if (result && result.data) {
        const apiUsers = result.data.map((u: any) => ({
          id: u.name,
          email: u.email,
          name: u.full_name || u.email,
          role: u.role === 'Administrator' ? 'admin' : 'support'
        }));
        setUsers(apiUsers);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  // Login function
  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      console.log('Login attempt:', email); // Debug log
      const result = await apiLogin(email, password);
      console.log('Login result:', result); // Debug log
      
      if (result && result.jwt) {
        const userData = {
          id: email,
          email: email,
          name: email,
          role: result.role === 'Administrator' ? 'admin' : 'support'
        };
        
        setUser(userData);
        setIsAuthenticated(true);
        
        // Save to localStorage
        try {
          localStorage.setItem('user', JSON.stringify(userData));
          localStorage.setItem('auth_token', result.jwt);
        } catch (error) {
          console.error('Error saving user to localStorage:', error);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };
  // Logout function
  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('user');
    localStorage.removeItem('auth_token');
    try {
      sessionStorage.removeItem('auth_expired');
      sessionStorage.removeItem('auth_expired_path');
    } catch {}
  };

  // Add user function
  const addUser = async (email: string, name: string, password: string, role: 'admin' | 'support'): Promise<boolean> => {
    try {
      const result = await createEsimAdminUser({
        email,
        password,
        status: 'Active',
        role: role === 'admin' ? 'Administrator' : 'Support Agent'
      });
      
      if (result && result.name) {
        // Reload users to get updated list
        await loadUsers();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Add user error:', error);
      return false;
    }
  };

  // Delete user function
  const deleteUser = async (id: string): Promise<boolean> => {
    try {
      // Prevent deleting yourself
      if (user?.id === id) {
        return false;
      }
      
      const result = await deleteEsimAdminUser(id);
      if (result) {
        // Reload users to get updated list
        await loadUsers();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Delete user error:', error);
      return false;
    }
  };

  // Update user role function
  const updateUserRole = async (id: string, role: 'admin' | 'support'): Promise<boolean> => {
    try {
      const result = await updateEsimAdminUser({
        name: id,
        role: role === 'admin' ? 'Administrator' : 'Support Agent'
      });
      
      if (result) {
        // Reload users to get updated list
        await loadUsers();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Update user role error:', error);
      return false;
    }
  };

  // Change password function
  const changePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    try {
      if (!user) return false;
      
      const result = await updateEsimAdminUser({
        name: user.id,
        password: newPassword
      });
      
      return result ? true : false;
    } catch (error) {
      console.error('Change password error:', error);
      return false;
    }
  };
  return <AuthContext.Provider value={{
    user,
    isAuthenticated,
    users,
    login,
    logout,
    addUser,
    deleteUser,
    updateUserRole,
    changePassword
  }}>
      {children}
    </AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);
